import React from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { LanguageProvider } from './context/LanguageContext';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { CartDrawer } from './components/cart/CartDrawer';
import { AdminLayout } from './components/layout/AdminLayout';
import { SupplierLayout } from './components/layout/SupplierLayout';
import { Home } from './pages/Home';
import { Catalogue } from './pages/Catalogue';
import { ProductDetail } from './pages/ProductDetail';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { Checkout } from './pages/Checkout';
import { ClientDashboard } from './pages/ClientDashboard';
import { ClientOrders } from './pages/ClientOrders';
import { OrderDetail } from './pages/OrderDetail';
import { ClientCatalogs } from './pages/ClientCatalogs';
import { AdminDashboard } from './pages/AdminDashboard';
import { AdminOrders } from './pages/AdminOrders';
import { AdminProducts } from './pages/AdminProducts';
import { AdminClients } from './pages/AdminClients';
import { AdminCatalogs } from './pages/AdminCatalogs';
import { AdminOffers } from './pages/AdminOffers';
import { AdminEmails } from './pages/AdminEmails';
import { AdminBestPractices } from './pages/AdminBestPractices';
import AdminManagement from './pages/AdminManagement';
import { AdminQuotes } from './pages/AdminQuotes';
import { AdminVisitors } from './pages/AdminVisitors';
import { AdminSuppliers } from './pages/AdminSuppliers';
import AdminSupplierUpdates from './pages/AdminSupplierUpdates';
import { AdminContacts } from './pages/AdminContacts';
import { ClientQuotes } from './pages/ClientQuotes';
import { ClientBestPractices } from './pages/ClientBestPractices';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import ClientProfile from './pages/ClientProfile';
import AdminProfile from './pages/AdminProfile';
import AdminOrderCalls from './pages/AdminOrderCalls';
import AdminCreateOrder from './pages/admin/AdminCreateOrder';
import SupplierDashboard from './pages/SupplierDashboard';
import SupplierProducts from './pages/SupplierProducts';
import SupplierOrders from './pages/SupplierOrders';
import SupplierMessages from './pages/SupplierMessages';
import SupplierDeliveryNotes from './pages/SupplierDeliveryNotes';
import SupplierProfile from './pages/SupplierProfile';
import AdminMessages from './pages/AdminMessages';
import AdminDeliveryNotes from './pages/AdminDeliveryNotes';
import { MentionsLegales } from './pages/MentionsLegales';
import { CGV } from './pages/CGV';
import { PolitiqueConfidentialite } from './pages/PolitiqueConfidentialite';
import { CookieBanner } from './components/CookieBanner';
import { Contact } from './pages/Contact';
import './App.css';

// Error Boundary to prevent white pages
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    componentDidCatch(error, info) {
        console.error('ErrorBoundary caught:', error, info);
    }
    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'sans-serif' }}>
                    <h2 style={{ color: '#1A4D2E' }}>Une erreur est survenue</h2>
                    <p style={{ color: '#666' }}>Veuillez rafraichir la page</p>
                    <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
                        style={{ padding: '10px 24px', background: '#1A4D2E', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', marginTop: '16px' }}>
                        Rafraichir
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

// Protected Route for authenticated users
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();
    if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Chargement...</div></div>;
    if (!isAuthenticated) return <Navigate to="/connexion" replace />;
    return children;
};

// Admin Route
const AdminRoute = ({ children }) => {
    const { isAdmin, isAuthenticated, loading } = useAuth();
    if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Chargement...</div></div>;
    if (!isAuthenticated) return <Navigate to="/connexion" replace />;
    if (!isAdmin) return <Navigate to="/dashboard" replace />;
    return children;
};

// Supplier Route
const SupplierRoute = ({ children }) => {
    const { isSupplier, isAuthenticated, loading } = useAuth();
    if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Chargement...</div></div>;
    if (!isAuthenticated) return <Navigate to="/connexion" replace />;
    if (!isSupplier) return <Navigate to="/dashboard" replace />;
    return children;
};

// Layout with Header and Footer (for clients)
const MainLayout = ({ children }) => (
    <>
        <Header />
        <main className="flex-1">{children}</main>
        <Footer />
    </>
);

// Layout without Footer (for admin dashboards - no cart)
const DashboardLayout = ({ children }) => (
    <>
        <Header />
        <main className="flex-1">{children}</main>
    </>
);

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/" element={<MainLayout><Home /></MainLayout>} />
            <Route path="/catalogue" element={<MainLayout><Catalogue /></MainLayout>} />
            <Route path="/produit/:id" element={<MainLayout><ProductDetail /></MainLayout>} />
            <Route path="/connexion" element={<Login />} />
            <Route path="/inscription" element={<Register />} />
            <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/mentions-legales" element={<MainLayout><MentionsLegales /></MainLayout>} />
            <Route path="/cgv" element={<MainLayout><CGV /></MainLayout>} />
            <Route path="/politique-confidentialite" element={<MainLayout><PolitiqueConfidentialite /></MainLayout>} />
            <Route path="/contact" element={<Contact />} />
            
            {/* Protected Routes */}
            <Route path="/commander" element={<ProtectedRoute><DashboardLayout><Checkout /></DashboardLayout></ProtectedRoute>} />
            
            {/* Client Dashboard */}
            <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout><ClientDashboard /></DashboardLayout></ProtectedRoute>} />
            <Route path="/dashboard/commandes" element={<ProtectedRoute><DashboardLayout><ClientOrders /></DashboardLayout></ProtectedRoute>} />
            <Route path="/dashboard/commande/:id" element={<ProtectedRoute><DashboardLayout><OrderDetail /></DashboardLayout></ProtectedRoute>} />
            <Route path="/dashboard/devis" element={<ProtectedRoute><DashboardLayout><ClientQuotes /></DashboardLayout></ProtectedRoute>} />
            <Route path="/dashboard/catalogues" element={<ProtectedRoute><ClientCatalogs /></ProtectedRoute>} />
            <Route path="/dashboard/bonnes-pratiques" element={<ProtectedRoute><ClientBestPractices /></ProtectedRoute>} />
            <Route path="/dashboard/profil" element={<ProtectedRoute><ClientProfile /></ProtectedRoute>} />
            
            {/* Admin Routes */}
            <Route path="/admin" element={<AdminRoute><AdminLayout><AdminDashboard /></AdminLayout></AdminRoute>} />
            <Route path="/admin/commandes" element={<AdminRoute><AdminLayout><AdminOrders /></AdminLayout></AdminRoute>} />
            <Route path="/admin/commandes/creer" element={<AdminRoute><AdminLayout><AdminCreateOrder /></AdminLayout></AdminRoute>} />
            <Route path="/admin/commande/:id" element={<AdminRoute><AdminLayout><OrderDetail /></AdminLayout></AdminRoute>} />
            <Route path="/admin/produits" element={<AdminRoute><AdminLayout><AdminProducts /></AdminLayout></AdminRoute>} />
            <Route path="/admin/clients" element={<AdminRoute><AdminLayout><AdminClients /></AdminLayout></AdminRoute>} />
            <Route path="/admin/catalogues" element={<AdminRoute><AdminLayout><AdminCatalogs /></AdminLayout></AdminRoute>} />
            <Route path="/admin/offres" element={<AdminRoute><AdminLayout><AdminOffers /></AdminLayout></AdminRoute>} />
            <Route path="/admin/bonnes-pratiques" element={<AdminRoute><AdminLayout><AdminBestPractices /></AdminLayout></AdminRoute>} />
            <Route path="/admin/emails" element={<AdminRoute><AdminLayout><AdminEmails /></AdminLayout></AdminRoute>} />
            <Route path="/admin/devis" element={<AdminRoute><AdminLayout><AdminQuotes /></AdminLayout></AdminRoute>} />
            <Route path="/admin/visiteurs" element={<AdminRoute><AdminLayout><AdminVisitors /></AdminLayout></AdminRoute>} />
            <Route path="/admin/fournisseurs" element={<AdminRoute><AdminLayout><AdminSuppliers /></AdminLayout></AdminRoute>} />
            <Route path="/admin/mises-a-jour-fournisseur" element={<AdminRoute><AdminLayout><AdminSupplierUpdates /></AdminLayout></AdminRoute>} />
            <Route path="/admin/contacts" element={<AdminRoute><AdminLayout><AdminContacts /></AdminLayout></AdminRoute>} />
            <Route path="/admin/administrateurs" element={<AdminRoute><AdminLayout><AdminManagement /></AdminLayout></AdminRoute>} />
            <Route path="/admin/profil" element={<AdminRoute><AdminLayout><AdminProfile /></AdminLayout></AdminRoute>} />
            <Route path="/admin/appel-commandes" element={<AdminRoute><AdminLayout><AdminOrderCalls /></AdminLayout></AdminRoute>} />
            <Route path="/admin/messages" element={<AdminRoute><AdminLayout><AdminMessages /></AdminLayout></AdminRoute>} />
            <Route path="/admin/bons-livraison" element={<AdminRoute><AdminLayout><AdminDeliveryNotes /></AdminLayout></AdminRoute>} />

            {/* Supplier Routes */}
            <Route path="/fournisseur" element={<SupplierRoute><SupplierLayout><SupplierDashboard /></SupplierLayout></SupplierRoute>} />
            <Route path="/fournisseur/produits" element={<SupplierRoute><SupplierLayout><SupplierProducts /></SupplierLayout></SupplierRoute>} />
            <Route path="/fournisseur/commandes" element={<SupplierRoute><SupplierLayout><SupplierOrders /></SupplierLayout></SupplierRoute>} />
            <Route path="/fournisseur/bons-livraison" element={<SupplierRoute><SupplierLayout><SupplierDeliveryNotes /></SupplierLayout></SupplierRoute>} />
            <Route path="/fournisseur/messages" element={<SupplierRoute><SupplierLayout><SupplierMessages /></SupplierLayout></SupplierRoute>} />
            <Route path="/fournisseur/profil" element={<SupplierRoute><SupplierLayout><SupplierProfile /></SupplierLayout></SupplierRoute>} />
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <HashRouter>
            <LanguageProvider>
                <AuthProvider>
                    <CartProvider>
                        <div className="min-h-screen flex flex-col">
                            <ErrorBoundary>
                                <AppRoutes />
                            </ErrorBoundary>
                            <CartDrawer />
                            <CookieBanner />
                        </div>
                        <Toaster position="top-right" richColors />
                    </CartProvider>
                </AuthProvider>
            </LanguageProvider>
        </HashRouter>
    );
}

export default App;
